import React, { useRef, useState, useMemo } from "react";
import api, { formatApiError } from "../lib/api";
import { refreshLeadOptions } from "../lib/useLeadOptions";
import { UploadSimple, FileCsv, X, CheckCircle, WarningCircle, ArrowDown, ClipboardText, Table } from "@phosphor-icons/react";
import { toast } from "sonner";

const SAMPLE_CSV = `customer_name,phone,source,status,branch,agent,notes
Anil Kumar,919876543210,Google Ads,New,Sadashivanagar,Agent 1,interested in package
Priya Sharma,919876543211,WhatsApp,Contacted,Mysore,agent2@leadflow.com,follow up tomorrow
Ravi M,919876543212,Referrals,Appointment Booked,RR Nagar,,
Sunita R,919876543213,338 Number,RNR 1,Hubli,Agent 3,no answer twice
Mahesh K,919876543214,400 Number,Qualified,Jayanagar,,sent brochure
`;

const SAMPLE_PASTE = `customer_name\tphone\tsource\tstatus
Anil Kumar\t919876543210\tGoogle Ads\tNew
Priya Sharma\t919876543211\tWhatsApp\tContacted
Ravi M\t919876543212\tReferrals\tAppointment Booked`;

export default function ImportLeadsModal({ open, onClose, onDone, branches, agents }) {
  const [tab, setTab] = useState("upload"); // 'upload' | 'paste'
  const [file, setFile] = useState(null);
  const [pasteText, setPasteText] = useState("");
  const [dedupe, setDedupe] = useState("phone_30d");
  const [defaultSource, setDefaultSource] = useState("direct_walkin");
  const [defaultBranchId, setDefaultBranchId] = useState("");
  const [defaultAgentId, setDefaultAgentId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  if (!open) return null;

  const reset = () => {
    setFile(null); setPasteText(""); setResult(null); setSubmitting(false);
  };

  const close = () => { reset(); onClose(); };

  const handleFile = (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error("Please choose a .csv file");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer?.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const submit = async () => {
    if (tab === "upload" && !file) {
      toast.error("Pick a CSV first");
      return;
    }
    if (tab === "paste" && !pasteText.trim()) {
      toast.error("Paste at least a header row + one data row");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (tab === "upload") fd.append("file", file);
      else fd.append("csv_text", pasteText);
      fd.append("dedupe", dedupe);
      fd.append("default_source", defaultSource);
      if (defaultBranchId) fd.append("default_branch_id", defaultBranchId);
      if (defaultAgentId) fd.append("default_agent_id", defaultAgentId);
      const { data } = await api.post("/leads/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) {
        toast.success(`Imported ${data.created} lead${data.created !== 1 ? "s" : ""}`);
        refreshLeadOptions(); // surface any new custom statuses/sources in filters
        onDone?.();
      } else if (data.skipped > 0 && data.errors.length === 0) {
        toast.message(`All ${data.skipped} rows skipped as duplicates`);
      } else {
        toast.error(`No leads imported · ${data.error_count} errors`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSubmitting(false);
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "leadflow_sample.csv";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={close}>
      <div
        className="bg-white border border-border w-full max-w-2xl max-h-[92vh] overflow-y-auto fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="import-modal"
      >
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Bulk import</div>
            <h2 className="font-display text-2xl font-bold tracking-tight leading-none">
              Upload leads · CSV
            </h2>
          </div>
          <button onClick={close} className="p-1.5 hover:bg-secondary"><X size={16} /></button>
        </div>

        {!result && (
          <div className="p-6 space-y-5">
            {/* Tabs */}
            <div className="flex border-b border-border -mx-6 -mt-6 px-6">
              <button
                onClick={() => setTab("upload")}
                data-testid="tab-upload"
                className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.15em] font-bold border-b-2 transition-colors ${
                  tab === "upload" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <UploadSimple size={14} /> Upload CSV
              </button>
              <button
                onClick={() => setTab("paste")}
                data-testid="tab-paste"
                className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.15em] font-bold border-b-2 transition-colors ${
                  tab === "paste" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                <ClipboardText size={14} /> Paste rows
              </button>
            </div>

            {tab === "upload" && (
              <>
            {/* Drop zone */}
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              data-testid="import-dropzone"
              className={`border-2 border-dashed cursor-pointer transition-colors px-6 py-10 text-center ${
                drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/40 hover:bg-secondary/30"
              }`}
            >
              <input
                ref={inputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="import-file-input"
              />
              {file ? (
                <div className="flex items-center justify-center gap-3" data-testid="import-file-chip">
                  <FileCsv size={28} className="text-primary" />
                  <div className="text-left">
                    <div className="text-sm font-semibold">{file.name}</div>
                    <div className="text-xs text-muted-foreground tabular-nums">{(file.size / 1024).toFixed(1)} KB</div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setFile(null); }}
                    className="ml-4 text-[10px] uppercase tracking-[0.15em] font-bold text-primary hover:underline"
                    data-testid="import-file-clear"
                  >
                    Change
                  </button>
                </div>
              ) : (
                <div>
                  <UploadSimple size={32} className="mx-auto text-muted-foreground" />
                  <div className="text-sm font-semibold mt-3">Drop your CSV here</div>
                  <div className="text-xs text-muted-foreground mt-1">or click to browse</div>
                </div>
              )}
            </div>

            <button
              onClick={downloadSample}
              data-testid="download-sample"
              className="text-xs uppercase tracking-[0.15em] font-bold text-primary hover:underline flex items-center gap-1.5"
            >
              <ArrowDown size={12} weight="bold" /> Download sample template
            </button>
              </>
            )}

            {tab === "paste" && (
              <PasteTab
                value={pasteText}
                onChange={setPasteText}
                sample={SAMPLE_PASTE}
              />
            )}

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
              <Field label="Duplicates (by phone, 30 days)">
                <select value={dedupe} onChange={(e) => setDedupe(e.target.value)}
                  data-testid="import-dedupe"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                  <option value="none">Allow duplicates</option>
                  <option value="phone_30d">Skip if same phone in last 30 days</option>
                </select>
              </Field>
              <Field label="Default source (if blank in row)">
                <select value={defaultSource} onChange={(e) => setDefaultSource(e.target.value)}
                  data-testid="import-default-source"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                  <option value="direct_walkin">Direct Walk-in</option>
                  <option value="google_ads">Google Ads</option>
                  <option value="meta_lead_form">Meta Lead Form</option>
                  <option value="referrals">Referrals</option>
                  <option value="whatsapp_enquiry">WhatsApp Enquiry</option>
                  <option value="confirmed_appointment">Confirmed Appointment</option>
                  <option value="338_number">338 Number</option>
                  <option value="400_number">400 Number</option>
                </select>
              </Field>
              <Field label="Default branch (optional)">
                <select value={defaultBranchId} onChange={(e) => setDefaultBranchId(e.target.value)}
                  data-testid="import-default-branch"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                  <option value="">— None —</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="Default agent (optional)">
                <select value={defaultAgentId} onChange={(e) => setDefaultAgentId(e.target.value)}
                  data-testid="import-default-agent"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                  <option value="">— None —</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </Field>
            </div>

            <div className="border-t border-border pt-3 text-xs text-muted-foreground space-y-1.5 leading-relaxed">
              <p>
                <span className="font-bold text-foreground">Required column:</span>{" "}
                <code className="font-mono bg-secondary px-1">customer_name</code> (or <code className="font-mono bg-secondary px-1">name</code> / <code className="font-mono bg-secondary px-1">full_name</code>).
              </p>
              <p>
                <span className="font-bold text-foreground">Optional:</span>{" "}
                <code className="font-mono bg-secondary px-1">phone</code>,{" "}
                <code className="font-mono bg-secondary px-1">source</code>,{" "}
                <code className="font-mono bg-secondary px-1">status</code> (New, Contacted, RNR 1, Appointment Booked, Qualified, Converted, Existing Client, …),{" "}
                <code className="font-mono bg-secondary px-1">branch</code> (id or name),{" "}
                <code className="font-mono bg-secondary px-1">agent</code> (id, email or name),{" "}
                <code className="font-mono bg-secondary px-1">notes</code>.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-border">
              <button onClick={close}
                className="px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary rounded-sm">
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={submitting || (tab === "upload" ? !file : !pasteText.trim())}
                data-testid="import-submit"
                className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm disabled:opacity-60 flex items-center gap-2"
              >
                {submitting ? "Importing…" : (tab === "upload" ? "Import leads" : `Import ${parsedPreview(pasteText)?.count || 0} rows`)}
              </button>
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="p-6 space-y-5" data-testid="import-result">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Created" value={result.created} color="#008A00" Icon={CheckCircle} />
              <Stat label="Skipped" value={result.skipped} color="#52525B" Icon={WarningCircle} />
              <Stat label="Errors" value={result.error_count} color={result.error_count > 0 ? "#E00000" : "#52525B"} Icon={WarningCircle} />
            </div>

            {result.errors?.length > 0 && (
              <div className="border border-border" data-testid="import-errors">
                <div className="px-4 py-2 border-b border-border text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground bg-secondary/40">
                  First {result.errors.length} errors
                </div>
                <ul className="max-h-56 overflow-y-auto divide-y divide-border text-sm">
                  {result.errors.map((e, i) => (
                    <li key={i} className="px-4 py-2 flex items-center gap-3">
                      <span className="font-mono text-xs text-muted-foreground tabular-nums w-12 shrink-0">row {e.row}</span>
                      <span className="text-foreground/80">{e.error}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t border-border">
              <button onClick={reset}
                data-testid="import-another"
                className="px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary rounded-sm">
                Import another file
              </button>
              <button onClick={close} data-testid="import-done"
                className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm">
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-foreground/70 block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Stat({ label, value, color, Icon }) {
  return (
    <div className="border border-border p-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</span>
        <Icon size={14} weight="fill" style={{ color }} />
      </div>
      <div className="font-display text-3xl font-bold tracking-tight mt-2 tabular-nums" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

// --- Paste tab + helpers ---
function parsedPreview(text) {
  if (!text || !text.trim()) return null;
  const lines = text.replace(/\r\n/g, "\n").split("\n").filter((l) => l.trim().length > 0);
  if (lines.length < 2) return { count: 0, headers: [], rows: [], total: 0 };
  // detect delimiter
  const first = lines[0];
  const delim = first.includes("\t") ? "\t" : (first.match(/;/g)?.length > first.match(/,/g)?.length ? ";" : ",");
  const split = (l) => l.split(delim).map((c) => c.trim());
  const headers = split(first);
  const rows = lines.slice(1).map(split);
  return { count: rows.length, headers, rows: rows.slice(0, 5), total: rows.length };
}

function PasteTab({ value, onChange, sample }) {
  const preview = useMemo(() => parsedPreview(value), [value]);
  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground leading-relaxed">
        Copy rows directly from <strong>Excel</strong>, <strong>Google Sheets</strong>, or any spreadsheet (TSV/CSV both work) — the first line must be a header row. WhatsApp message lists & plain lists work too as long as the first line names the columns.
      </div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid="paste-textarea"
        rows={9}
        spellCheck={false}
        placeholder={sample}
        className="w-full px-3 py-2 border border-border rounded-sm bg-white text-xs font-mono leading-relaxed focus:border-primary focus:outline-none resize-y"
      />
      <div className="flex items-center justify-between">
        <button
          onClick={() => onChange(sample)}
          data-testid="load-paste-sample"
          className="text-xs uppercase tracking-[0.15em] font-bold text-primary hover:underline"
        >
          Use sample
        </button>
        {value && (
          <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground tabular-nums" data-testid="paste-row-count">
            {preview?.total || 0} row{(preview?.total || 0) !== 1 && "s"} detected
          </span>
        )}
      </div>

      {preview && preview.rows.length > 0 && (
        <div className="border border-border overflow-x-auto" data-testid="paste-preview">
          <div className="px-3 py-2 border-b border-border bg-secondary/40 flex items-center gap-2">
            <Table size={14} className="text-muted-foreground" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Preview · first {preview.rows.length} of {preview.total}
            </span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                {preview.headers.map((h, i) => (
                  <th key={i} className="px-3 py-2 font-bold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((r, i) => (
                <tr key={i} className="border-b border-border last:border-0">
                  {preview.headers.map((_, j) => (
                    <td key={j} className="px-3 py-1.5 whitespace-nowrap text-foreground/80">
                      {r[j] || <span className="text-muted-foreground/60">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

